import { useEffect, useMemo, useRef, useState } from "react";
import { Client, imageUrl, isVector, Media } from "../studio.ts";

const SRCSET_WIDTHS = [480, 640, 960, 1280, 1600, 2000, 2400];

interface GalleryItem {
	media: Media;
	client: Client;
	order: number;
}

const FrameVideo = ({ media }: { media: Media }) => {
	const ref = useRef<HTMLVideoElement>(null);
	const [near, setNear] = useState(false);

	// Nothing downloads (not even the poster) until the video is within a
	// screen of view, like the images' native lazy loading
	useEffect(() => {
		const video = ref.current;
		if (!video || near) return;
		const observer = new IntersectionObserver(
			([entry]) => entry.isIntersecting && setNear(true),
			{ rootMargin: "100% 0px" }
		);
		observer.observe(video);
		return () => observer.disconnect();
	}, [near]);

	// Plays only while on screen
	useEffect(() => {
		const video = ref.current;
		if (!video || !near) return;
		if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
		const observer = new IntersectionObserver(([entry]) => {
			if (entry.isIntersecting) video.play().catch(() => {});
			else video.pause();
		});
		observer.observe(video);
		return () => observer.disconnect();
	}, [near]);

	return (
		<video
			ref={ref}
			src={near ? media.url : undefined}
			poster={near && media.posterUrl ? imageUrl(media.posterUrl, 1280) : undefined}
			aria-label={media.alt}
			muted
			loop
			playsInline
			preload={near ? "metadata" : "none"}
		/>
	);
};

/**
 * The given clients' media, newest first, each fitted into a grey 5:4
 * frame. A banner pinned at the middle of the screen names whichever piece
 * is passing under it.
 */
export const Gallery = ({ clients }: { clients: Client[] }) => {
	const [current, setCurrent] = useState<number | null>(null);
	const frames = useRef<(HTMLElement | null)[]>([]);
	const [viewport, setViewport] = useState(() => ({
		width: window.innerWidth,
		height: window.innerHeight,
	}));

	// Touch browsers resize the viewport height as their toolbars collapse
	// mid-scroll; only a width change is a real resize
	useEffect(() => {
		const onResize = () =>
			setViewport((v) =>
				window.innerWidth === v.width
					? v
					: { width: window.innerWidth, height: window.innerHeight }
			);
		window.addEventListener("resize", onResize);
		return () => window.removeEventListener("resize", onResize);
	}, []);

	const items = useMemo<GalleryItem[]>(
		() =>
			clients
				.flatMap((client) =>
					client.media
						.filter((media) => media.width && media.height)
						.map((media, order) => ({ media, client, order }))
				)
				// Newest first; one client's same-day items keep their upload order
				.sort(
					(a, b) =>
						(b.media.date ?? "").localeCompare(a.media.date ?? "") ||
						a.client.name.localeCompare(b.client.name) ||
						a.order - b.order
				),
		[clients]
	);

	// The root is shrunk to a 3px strip at the screen's middle (taller than
	// the 1px gaps between frames), so a frame "intersects" exactly while
	// it's under the banner. The browser tracks this itself, so nothing runs
	// per scroll frame.
	useEffect(() => {
		const under = new Set<number>();
		const above = Math.floor(viewport.height / 2) - 1;
		const below = viewport.height - above - 3;
		const observer = new IntersectionObserver(
			(entries) => {
				for (const entry of entries) {
					const index = frames.current.indexOf(entry.target as HTMLElement);
					if (index < 0) continue;
					if (entry.isIntersecting) under.add(index);
					else under.delete(index);
				}
				setCurrent(under.size ? Math.max(...under) : null);
			},
			{ rootMargin: `-${above}px 0px -${below}px 0px` }
		);
		frames.current.length = items.length;
		frames.current.forEach((frame) => frame && observer.observe(frame));
		return () => observer.disconnect();
	}, [items, viewport]);

	// The gallery pads its ends by half a screen minus half a frame (in CSS)
	// so the first and last pieces can reach the banner at mid-screen; the
	// frame height comes from layout, so it's measured
	const [gallery, setGallery] = useState<HTMLElement | null>(null);
	useEffect(() => {
		const frame = gallery?.querySelector(".nh-frame");
		if (!gallery || !frame) return;
		const observer = new ResizeObserver(([entry]) =>
			gallery.style.setProperty(
				"--nh-frame-h",
				`${Math.round(entry.borderBoxSize?.[0]?.blockSize ?? entry.contentRect.height)}px`
			)
		);
		observer.observe(frame);
		return () => observer.disconnect();
	}, [gallery, items]);

	// Keeps naming the last piece while the banner fades out
	const lastShown = useRef(0);
	if (current !== null) lastShown.current = current;

	if (!items.length) return null;

	const { media, client } = items[Math.min(lastShown.current, items.length - 1)];

	return (
		<section ref={setGallery} className="nh-gallery fade-in" aria-label="Work">
			{/* Zero-height and sticky, so it rides at the screen's middle only
			    while the gallery is under it, with no scroll scripting */}
			<div
				className={`nh-gallery-banner${current === null ? " is-hidden" : ""}`}
				aria-hidden="true"
			>
				<div className="nh-gallery-banner-row">
					<p className="nh-gallery-banner-client">{client.name}</p>
					{media.service ? <p>{media.service}</p> : null}
					{media.credit ? <p className="nh-gallery-banner-credit">{media.credit}</p> : null}
				</div>
			</div>
			{items.map(({ media, client }, index) => (
				<figure
					key={`${client.slug}-${media.key}`}
					ref={(el) => {
						frames.current[index] = el;
					}}
					className="nh-frame"
				>
					{media.kind === "video" ? (
						<FrameVideo media={media} />
					) : (
						<img
							src={imageUrl(media.url, 1280)}
							srcSet={
								isVector(media.url)
									? undefined
									: [...SRCSET_WIDTHS.filter((w) => w < media.width!), media.width!]
											.map((w) => `${imageUrl(media.url, w)} ${w}w`)
											.join(", ")
							}
							sizes={isVector(media.url) ? undefined : "(max-width: 900px) 100vw, 50vw"}
							width={media.width}
							height={media.height}
							alt={media.alt ? `${client.name}: ${media.alt}` : client.name}
							loading={index < 2 ? "eager" : "lazy"}
							decoding="async"
							draggable={false}
						/>
					)}
				</figure>
			))}
		</section>
	);
};
